import { use, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { LiteraryFeaturesDataContext } from '@/data/literary-features/literary-feature-data-context';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SourceLinks } from '@/components/ui/source-links';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { formatChiasmPattern } from './literary-context-utils';
import { ThemeTrajectoryList, PatternList } from './literary-context-components';
import { HorizontalContextNavigator } from './HorizontalContextNavigator';
import { BookOutlineSection } from './book-outline-section';
import { Library, Sparkles, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { getOutlinesByBook } from '@/data/outline/outline-data-service';
import { buildVisibleNodeIds } from '@/lib/outline/outline-tree-utils';
import { GenreInfoModal } from './GenreInfoModal';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { DiscoveryCard } from './discovery-card';
import { ContextHero } from '../context/ContextHero';
import { normalizeBookName } from '@/lib/utils/bible-utils';

// Hero illustration
import bookStructureHero from '@/assets/heroes/book_structure_hero_v2.png';

interface Props {
    passageId: string;
    isTransitionAnimating?: boolean;
}
import { cn } from '@/lib/utils';

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <div className="h-40 rounded-xl bg-muted animate-pulse mb-6" />
            <div className="space-y-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-4">
                        <div className="w-0.5 bg-muted h-20" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-6 w-48" />
                            <Skeleton className="h-20 w-full" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="space-y-4 p-4">
            <div className="rounded-xl border border-dashed p-12 flex flex-col items-center justify-center text-center space-y-3">
                <BookOpen className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                    No passage data available for structural analysis.
                </p>
            </div>
        </div>
    );
}

export default function PassageBookContext({ passageId, isTransitionAnimating }: Props) {
    const passageContext = use(PassageDataContext);
    const literaryFeaturesContext = use(LiteraryFeaturesDataContext);
    const outlineContext = use(OutlineDataContext);
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['outline', 'macro-structure', 'pivots-hinges', 'theme-trajectory']));
    const [genreModalOpen, setGenreModalOpen] = useState(false);
    const [selectedGenreInfo, setSelectedGenreInfo] = useState<{ form: string | undefined; genre: string | undefined }>({ form: undefined, genre: undefined });

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const bookFeatures = useMemo(() => {
        if (!literaryFeaturesContext || !passage) {
            return null;
        }
        return literaryFeaturesContext.getLiteraryFeaturesByBook(passage.book);
    }, [literaryFeaturesContext, passage]);

    const allPassages = useMemo(() => {
        return passageContext.data?.items || [];
    }, [passageContext.data]);

    // Create a map of passage ID to passage for quick lookup
    const passageMap = useMemo(() => {
        const map = new Map<string, Passage>();
        allPassages.forEach(p => map.set(p.id, p));
        return map;
    }, [allPassages]);

    const bookOutline = useMemo(() => {
        if (!outlineContext?.data || !passage) {
            return null;
        }
        const outlinesByBook = getOutlinesByBook(outlineContext.data.items);
        const bookOutlines = outlinesByBook.get(passage.book) || [];
        return bookOutlines[0]; // Assuming one outline per book
    }, [outlineContext?.data, passage]);

    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const summaryRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const checkOverflow = () => {
            if (summaryRef.current) {
                if (!isSummaryExpanded) {
                    setIsOverflowing(summaryRef.current.scrollHeight > summaryRef.current.offsetHeight);
                }
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [isSummaryExpanded, bookOutline?.summary]);

    // Build the set of visible node IDs for the filtered view
    const visibleNodeIds = useMemo(() => {
        if (!bookOutline) return new Set<string>();
        return buildVisibleNodeIds(bookOutline.structure, passageId);
    }, [bookOutline, passageId]);

    const toggleSection = useCallback((section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    }, []);

    // Pre-calculate macro structure chiasm formatting
    const formattedMacroStructures = useMemo(() => {
        if (!bookFeatures?.macroStructure) return [];

        return bookFeatures.macroStructure.map(structure => {
            const isChiasm = structure.type.toLowerCase() === 'chiastic' && structure.chiasm?.pattern;
            return {
                ...structure,
                isChiasm,
                chiasmElements: isChiasm ? formatChiasmPattern(structure.chiasm!.pattern) : []
            };
        });
    }, [bookFeatures?.macroStructure]);

    if (!passageContext.data) {
        return <LoadingSkeleton />;
    }

    if (!passage) {
        return <EmptyState />;
    }

    return (
        <div className="relative">


            <div className="relative flex flex-col min-h-full">
                {/* Cinematic Hero Card */}
                <ContextHero
                    eyebrow="BOOK CONTEXT"
                    headline="The Macro View"
                    subtitle="Understand the overarching structure and flow of the book to keep the passage in its proper lens."
                    image={bookStructureHero}
                    metadata={passage.genre ? { genre: passage.genre } : undefined}
                    onClose={() => setUrl(`/app/passage/${passageId}`)}
                    showClose={false}
                    className="mb-0 rounded-b-none"
                />

                {/* Canon Navigator - Positioned as a divider underneath the hero */}
                <HorizontalContextNavigator
                    mode="book"
                    passage={passage}
                    allPassages={allPassages}
                    outlineData={outlineContext.data?.items}
                    setUrl={setUrl}
                    sticky={true}
                    className="z-40"
                    isTransitionAnimating={isTransitionAnimating}
                />

                <div className="px-6 pt-4 pb-20">
                    {/* Book Overview Section */}
                    {bookOutline?.summary && (
                        <div className="mb-8 relative group">
                            <p
                                ref={summaryRef}
                                className={cn(
                                    "text-lg leading-relaxed text-foreground/80 italic border-l-2 border-primary/20 pl-6 transition-all duration-300",
                                    !isSummaryExpanded && "line-clamp-3"
                                )}
                            >
                                {bookOutline.summary}
                            </p>
                            {(isOverflowing || isSummaryExpanded) && (
                                <button
                                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                                    className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 hover:text-primary transition-colors mt-2 ml-6 flex items-center gap-1"
                                >
                                    {isSummaryExpanded ? (
                                        <>Show Less <ChevronUp className="h-3 w-3" /></>
                                    ) : (
                                        <>Show More <ChevronDown className="h-3 w-3" /></>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="space-y-8 pr-1">
                        {/* Section 1: Structure & Architecture */}
                        <div className="space-y-6">
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Structure & Architecture
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        The Framework
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            {/* Book Outline */}
                            {bookOutline && (
                                <DiscoveryCard
                                    isExpanded={expandedSections.has('outline')}
                                    onToggle={() => toggleSection('outline')}
                                    title={`Outline of ${normalizeBookName(passage.book)}`}
                                    icon={Library}
                                >
                                    <div className="mt-4">
                                        <BookOutlineSection
                                            outline={bookOutline}
                                            passageId={passageId}
                                            passageMap={passageMap}
                                            visibleNodeIds={visibleNodeIds}
                                            onGenreClick={(form, genre) => {
                                                setSelectedGenreInfo({ form, genre });
                                                setGenreModalOpen(true);
                                            }}
                                        />
                                    </div>
                                </DiscoveryCard>
                            )}

                            {/* Macro Structure */}
                            {bookFeatures?.macroStructure && (
                                <DiscoveryCard
                                    isExpanded={expandedSections.has('macro-structure')}
                                    onToggle={() => toggleSection('macro-structure')}
                                    title={`Macro Structures`}
                                    icon={Library}
                                >
                                    <div className="mt-4 space-y-6">
                                        <p className="text-sm text-muted-foreground italic font-light">
                                            Major structural patterns organizing {normalizeBookName(passage.book)}
                                        </p>
                                        <div className="space-y-6">
                                            {formattedMacroStructures.map((structure, index) => (
                                                <div key={index} className="space-y-3 relative pb-2 group/struct">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="space-y-1">
                                                            <h5 className="font-serif text-lg font-medium text-foreground/90">
                                                                {structure.type}
                                                            </h5>
                                                            <div className="flex items-center gap-3">
                                                                {structure.chiasm?.confidence && (
                                                                    <ConfidenceBadge confidence={structure.chiasm.confidence} />
                                                                )}
                                                                {structure.isPrimary && (
                                                                    <Badge variant="secondary" className="text-[9px] uppercase tracking-widest bg-primary/5 border-primary/20 text-primary/70">Primary</Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {structure.chiasm?.range && (
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                                                                {structure.chiasm.range}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <p className="text-sm leading-relaxed text-foreground/80 font-light">
                                                        {structure.description}
                                                    </p>

                                                    {structure.chiasm && structure.isChiasm ? (
                                                        <div className="bg-primary/[0.02] border border-primary/5 rounded-lg p-4 mt-2 space-y-1.5">
                                                            {structure.chiasmElements.map((element, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    style={{ paddingLeft: `${element.level * 1.5}rem` }}
                                                                    className="text-xs flex gap-2"
                                                                >
                                                                    <span className="font-bold text-primary/60 min-w-[2rem]">{element.label}</span>
                                                                    {element.content && <span className="text-muted-foreground leading-relaxed italic">{element.content}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : structure.chiasm && !structure.isChiasm ? (
                                                        <div className="p-3 bg-muted/30 rounded-lg text-xs font-light italic text-muted-foreground border border-border/40">
                                                            <span className="font-medium text-foreground/70 not-italic uppercase tracking-widest mr-2">Pattern:</span>
                                                            {structure.chiasm.pattern}
                                                        </div>
                                                    ) : null}

                                                    {structure.ring && (
                                                        <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-1 border border-border/40">
                                                            <div className="flex justify-between font-serif mb-1">
                                                                <span className="text-primary/70">Ring Structure</span>
                                                                <span className="text-muted-foreground/60 italic">{structure.ring.range}</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div>
                                                                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 mb-0.5">Opening Theme</div>
                                                                    <div className="italic text-foreground/80">{structure.ring.openingTheme}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 mb-0.5">Closing Theme</div>
                                                                    <div className="italic text-foreground/80">{structure.ring.closingTheme}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {structure?.sources?.length > 0 && (
                                                        <div className="pt-2">
                                                            <SourceLinks sources={structure.sources} />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </DiscoveryCard>
                            )}
                        </div>

                        {/* Section 2: Narrative Flow & Movement */}
                        {(bookFeatures?.themeTrajectory?.length > 0 || bookFeatures?.pivotsAndHinges?.length > 0) && (
                            <div className="space-y-6 pt-4">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                        Narrative Flow & Movement
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                            Development & Shifts
                                        </span>
                                        <div className="h-px w-8 bg-primary/20" />
                                    </div>
                                </div>

                                {/* Theme Trajectory */}
                                {bookFeatures?.themeTrajectory?.length > 0 && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('theme-trajectory')}
                                        onToggle={() => toggleSection('theme-trajectory')}
                                        title={`Theme Development`}
                                        icon={Sparkles}
                                    >
                                        <div className="mt-4">
                                            <ThemeTrajectoryList
                                                themeTrajectories={bookFeatures.themeTrajectory}
                                                book={passage.book}
                                                passage={passage}
                                                isDarkMode={isDarkMode}
                                                setUrl={setUrl}
                                                allPassages={allPassages}
                                            />
                                        </div>
                                    </DiscoveryCard>
                                )}

                                {/* Pivots and Hinges */}
                                {bookFeatures?.pivotsAndHinges?.length > 0 && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('pivots-hinges')}
                                        onToggle={() => toggleSection('pivots-hinges')}
                                        title={`Pivots & turning points`}
                                        icon={Library}
                                    >
                                        <div className="mt-4 space-y-6">
                                            <p className="text-sm text-muted-foreground italic font-light">
                                                Key moments that shift the narrative or theological direction of {normalizeBookName(passage.book)}
                                            </p>
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                {bookFeatures.pivotsAndHinges.map((pivot, index) => (
                                                    <div key={index} className="p-4 rounded-xl bg-card border border-border/40 hover:border-primary/20 transition-all group/pivot shadow-sm">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span
                                                                className="text-lg font-serif font-medium text-primary hover:text-primary/80 cursor-pointer transition-colors"
                                                                onClick={() => setUrl(`/${pivot.ref}`)}
                                                            >
                                                                {pivot.ref}
                                                            </span>
                                                            <div className="flex gap-2">
                                                                {pivot.importance && <ImportanceBadge importance={pivot.importance} className="h-4 text-[8px]" />}
                                                            </div>
                                                        </div>
                                                        <p className="text-xs leading-relaxed text-muted-foreground italic font-light group-hover/pivot:text-foreground/80 transition-colors line-clamp-3">
                                                            {pivot.why}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </DiscoveryCard>
                                )}
                            </div>
                        )}

                        {/* Section 3: Compositional Mechanics */}
                        {(bookFeatures?.seamsAndFrames?.length > 0 || (bookFeatures?.repeatedPatterns && bookFeatures.repeatedPatterns.length > 0)) && (
                            <div className="space-y-6 pt-4">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                        Compositional Mechanics
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                            Texture & Cohesion
                                        </span>
                                        <div className="h-px w-8 bg-primary/20" />
                                    </div>
                                </div>

                                {/* Seams and Frames */}
                                {bookFeatures?.seamsAndFrames?.length > 0 && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('seams-frames')}
                                        onToggle={() => toggleSection('seams-frames')}
                                        title={`Seams & Frames`}
                                        icon={Library}
                                    >
                                        <div className="mt-4 space-y-4">
                                            {bookFeatures.seamsAndFrames.map((frame, index) => (
                                                <div key={index} className="relative pl-4 border-l border-primary/20 space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <h5 className="font-serif font-medium text-foreground/90">{frame.device}</h5>
                                                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">{frame.startRef} — {frame.endRef}</span>
                                                    </div>
                                                    {frame.note && <p className="text-sm font-light leading-relaxed text-foreground/70 italic">{frame.note}</p>}
                                                    {frame.importance && <ImportanceBadge importance={frame.importance} className="h-4 text-[9px]" />}
                                                </div>
                                            ))}
                                        </div>
                                    </DiscoveryCard>
                                )}

                                {/* Repeated Patterns */}
                                {bookFeatures?.repeatedPatterns && bookFeatures.repeatedPatterns.length > 0 && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('patterns-book')}
                                        onToggle={() => toggleSection('patterns-book')}
                                        title={`Repeated Patterns`}
                                        icon={Sparkles}
                                    >
                                        <div className="mt-4">
                                            <PatternList
                                                patterns={bookFeatures.repeatedPatterns}
                                                book={passage.book}
                                                isDarkMode={isDarkMode}
                                                setUrl={setUrl}
                                                filterByPassage={false}
                                            />
                                        </div>
                                    </DiscoveryCard>
                                )}
                            </div>
                        )}
                    </div>

                    {/* AI Disclaimer */}
                    <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                        <AIDisclaimerBadge
                            model={bookFeatures?.generatedBy || (bookFeatures as any)?.source}
                            passageId={passageId}
                            contextType="book-context"
                        />
                        <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                            This structural analysis is synthesized by the BibleGlen Context Module, utilizing Gemini 3.0 Pro. Canonical patterns optimized for devotional and academic depth.
                        </p>
                    </div>

                    {/* Genre Info Modal */}
                    <GenreInfoModal
                        isOpen={genreModalOpen}
                        onClose={() => setGenreModalOpen(false)}
                        form={selectedGenreInfo.form}
                        genre={selectedGenreInfo.genre}
                    />
                </div>
            </div>
        </div>
    );
}

